<script>
    let {
        placeholder = '',
        name,
        value = $bindable(''),
        required = false,
        disabled = false,
        readonly = false,
        results = [],
        loading = false,
        loadingLabel = 'Searching...',
        noResultsLabel = 'No matching results found.',
        showCreateNew = true,
        createNewLabel = 'Create New',
        onsearch = () => {},
        onselect = () => {},
    } = $props();

    let isOpen = $state(false);
    let focusedIndex = $state(-1);
    let inputRef = $state(null);
    let containerRef = $state(null);

    function handleInput(e) {
        value = e.target.value;
        isOpen = true;
        focusedIndex = -1;
        onsearch(value);
    }

    function handleFocus() {
        if (value && !readonly) {
            isOpen = true;
        }
    }

    function handleKeydown(e) {
        if (!isOpen) {
            if (e.key === 'ArrowDown') {
                isOpen = true;
                e.preventDefault();
            }
            return;
        }

        const totalItems = results.length + (showCreateNew ? 1 : 0);

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                focusedIndex = (focusedIndex + 1) % totalItems;
                break;
            case 'ArrowUp':
                e.preventDefault();
                focusedIndex = focusedIndex <= 0 ? totalItems - 1 : focusedIndex - 1;
                break;
            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < results.length) {
                    selectResult(results[focusedIndex]);
                } else if (focusedIndex === results.length && showCreateNew) {
                    selectCreateNew();
                }
                break;
            case 'Escape':
                isOpen = false;
                focusedIndex = -1;
                break;
        }
    }

    function selectResult(item) {
        value = item.title || item.name || '';
        isOpen = false;
        focusedIndex = -1;
        onselect(item);
    }

    function selectCreateNew() {
        isOpen = false;
        focusedIndex = -1;
        onselect({ id: null, title: value });
    }

    function handleDocumentClick(e) {
        if (containerRef && !containerRef.contains(e.target)) {
            isOpen = false;
            focusedIndex = -1;
        }
    }

    $effect(() => {
        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    });

    const showDropdown = $derived(isOpen && !readonly && (loading || results.length > 0 || showCreateNew));
</script>

<div class="autocomplete-input" bind:this={containerRef}>
    <div class="input-wrapper">
        <input
            class="input"
            {name}
            type="text"
            {value}
            {required}
            {disabled}
            {readonly}
            {placeholder}
            oninput={handleInput}
            onfocus={handleFocus}
            onkeydown={handleKeydown}
            bind:this={inputRef}
            autocomplete="off"
        />
        {#if !readonly}
            <i class="material-icons search-icon" aria-hidden="true">search</i>
        {/if}
    </div>

    {#if showDropdown}
        <div class="dropdown">
            {#if loading}
                <div class="dropdown-item status">{loadingLabel}</div>
            {:else if results.length === 0 && value}
                <div class="dropdown-item status">{noResultsLabel}</div>
            {/if}

            {#each results as item, idx}
                <button
                    type="button"
                    class="dropdown-item result"
                    class:focused={focusedIndex === idx}
                    onmouseenter={() => focusedIndex = idx}
                    onclick={() => selectResult(item)}
                >
                    <span class="result-title">{item.title || item.name || ''}</span>
                    {#if item.subtitle}
                        <span class="result-subtitle">{item.subtitle}</span>
                    {/if}
                </button>
            {/each}

            {#if showCreateNew}
                <button
                    type="button"
                    class="dropdown-item create-new"
                    class:focused={focusedIndex === results.length}
                    onmouseenter={() => focusedIndex = results.length}
                    onclick={selectCreateNew}
                >
                    <i class="material-icons" aria-hidden="true">add</i>
                    <span>{createNewLabel}</span>
                </button>
            {/if}
        </div>
    {/if}
</div>

<style>
    .autocomplete-input {
        display: flex;
        flex-direction: column;
        flex-grow: 10;
        width: 100%;
        height: 100%;
        max-width: 24rem;
        position: relative;
    }

    .input-wrapper {
        display: flex;
        align-items: center;
        width: 100%;
        position: relative;
    }

    .input {
        padding: 0.75rem;
        padding-right: 2.5rem;
        background-color: rgba(0, 0, 0, 0.6);
        color: #eee;
        border-radius: 0.35rem;
        border: solid 1px rgba(0, 0, 0, 0.1);
        outline-width: 1px;
        width: 100%;
        box-sizing: initial;
    }

    .input:valid {
        outline-style: solid;
        outline-color: rgb(74, 198, 255);
    }

    .input:invalid:not(:placeholder-shown) {
        outline-style: solid;
        outline-color: rgb(255, 192, 74);
    }

    .input:focus {
        outline-width: 3px;
    }

    .search-icon {
        position: absolute;
        right: 8px;
        color: rgba(255, 255, 255, 0.5);
        pointer-events: none;
        font-size: 1.2rem;
    }

    .dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 4px;
        background: #1a1a1a;
        border: solid 1px rgba(255, 255, 255, 0.12);
        border-radius: 0.5rem;
        z-index: 100;
        max-height: 16rem;
        overflow-y: auto;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    }

    .dropdown-item {
        width: 100%;
        text-align: left;
        padding: 0.6rem 0.75rem;
        border: none;
        background: none;
        color: #eee;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        font-size: 0.9rem;
    }

    .dropdown-item.status {
        color: rgba(255, 255, 255, 0.5);
        cursor: default;
        font-style: italic;
    }

    .dropdown-item.result:hover,
    .dropdown-item.result.focused {
        background: rgba(255, 206, 44, 0.12);
    }

    .dropdown-item.create-new {
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        border-top: solid 1px rgba(255, 255, 255, 0.08);
        color: rgb(255, 206, 44);
        font-weight: 500;
    }

    .dropdown-item.create-new:hover,
    .dropdown-item.create-new.focused {
        background: rgba(255, 206, 44, 0.12);
    }

    .dropdown-item.create-new i {
        font-size: 1.1rem;
    }

    .result-title {
        font-weight: 500;
        color: #fff;
    }

    .result-subtitle {
        font-size: 0.8rem;
        color: rgba(255, 255, 255, 0.5);
    }
</style>
